import { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FavoriteButton } from '../../../components/feature/FavoriteButton';
import type { PrefectureShop } from '../../../hooks/usePrefectureShops';
import { supabase } from '../../../lib/supabase';

interface ShopListProps {
  shops: PrefectureShop[];
  loading?: boolean;
  selectedShopId: string | number | null;
  onSelectShopId: (id: string | number | null) => void;
}

interface ShopAttribute {
  id: string;
  name: string;
  color: string;
}

interface ShopGenre {
  id: string;
  name: string;
  attribute_id: string;
}

interface ShopPart {
  id: string;
  name: string;
  main_genre_id: string;
}

interface EnrichedShop extends PrefectureShop {
  attributeName?: string;
  attributeColor?: string;
  mainGenreName?: string;
  mainPartName?: string;
}

const attributeIconMap: Record<string, string> = {
  'ライブハウス': 'ri-music-2-line',
  'スタジオ': 'ri-mic-line',
  '飲食店': 'ri-restaurant-line',
  'クラブ': 'ri-disc-line',
  '楽器店': 'ri-guitar-line',
  'ギャラリー': 'ri-gallery-line',
  'ショップ': 'ri-store-2-line',
};

const categoryIconMap: Record<string, string> = {
  'ライブ': 'ri-music-2-line',
  'スタジオ': 'ri-mic-line',
  '飲食店': 'ri-restaurant-line',
  'クラブ': 'ri-disc-line',
  '楽器店': 'ri-guitar-line',
  'カフェ': 'ri-cup-line',
  'レストラン': 'ri-restaurant-line',
  'バー': 'ri-goblet-line',
  'ギャラリー': 'ri-gallery-line',
  'ショップ': 'ri-store-2-line',
  'その他': 'ri-more-line',
};

export function ShopList({ shops, loading, selectedShopId, onSelectShopId }: ShopListProps) {
  const [activeCategory, setActiveCategory] = useState('ALL');
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [attributes, setAttributes] = useState<ShopAttribute[]>([]);
  const [genres, setGenres] = useState<ShopGenre[]>([]);
  const [parts, setParts] = useState<ShopPart[]>([]);
  const [enrichedShops, setEnrichedShops] = useState<EnrichedShop[]>([]);
  const [loadingHierarchy, setLoadingHierarchy] = useState(true);

  // 階層データを取得
  useEffect(() => {
    const fetchHierarchy = async () => {
      setLoadingHierarchy(true);
      try {
        const [attrRes, genreRes, partRes] = await Promise.all([
          supabase.from('shop_attributes').select('*').order('sort_order'),
          supabase.from('shop_main_genres').select('*').order('sort_order'),
          supabase.from('shop_main_parts').select('*').order('sort_order'),
        ]);

        if (attrRes.data) setAttributes(attrRes.data);
        if (genreRes.data) setGenres(genreRes.data);
        if (partRes.data) setParts(partRes.data);
      } catch (error) {
        console.error('Error fetching hierarchy:', error);
      } finally {
        setLoadingHierarchy(false);
      }
    };

    fetchHierarchy();
  }, []);

  // 店舗データを階層情報で拡張
  useEffect(() => {
    if (loadingHierarchy) return;

    const enriched: EnrichedShop[] = shops.map((shop) => {
      const attr = attributes.find((a) => a.id === shop.attributeId);
      const genre = genres.find((g) => g.id === shop.mainGenreId);
      const part = parts.find((p) => p.id === shop.mainPartId);

      return {
        ...shop,
        attributeName: attr?.name,
        attributeColor: attr?.color || shop.color,
        mainGenreName: genre?.name,
        mainPartName: part?.name,
      };
    });

    setEnrichedShops(enriched);
  }, [shops, attributes, genres, parts, loadingHierarchy]);

  // カテゴリ一覧（属性ベース）
  const categories = useMemo(() => {
    const uniqueAttrs = Array.from(
      new Set(enrichedShops.map((s) => s.attributeName).filter(Boolean))
    );
    return ['ALL', ...uniqueAttrs];
  }, [enrichedShops]);

  const getCategoryIcon = (cat: string) => {
    if (cat === 'ALL') return 'ri-apps-line';
    return attributeIconMap[cat] || categoryIconMap[cat] || 'ri-store-2-line';
  };

  const getCategoryColor = (cat: string) => {
    if (cat === 'ALL') return '#00ff41';
    const shop = enrichedShops.find((s) => s.attributeName === cat);
    return shop?.attributeColor || '#00ff41';
  };

  const filteredShops = activeCategory === 'ALL'
    ? enrichedShops
    : enrichedShops.filter((s) => s.attributeName === activeCategory);

  const handleCategoryClick = (cat: string) => {
    setActiveCategory(cat);
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  const handleShopClick = (shop: EnrichedShop) => {
    navigate(`/shop/${shop.userId}`);
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 lg:mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1.5 h-1.5 rounded-full bg-neon-pink" style={{ boxShadow: '0 0 8px #ff006e' }} />
            <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">Registered Shops</span>
          </div>
          <h3 className="text-white font-display text-xl lg:text-2xl tracking-wider">登録店舗一覧</h3>
        </div>
        <span className="text-neon-green text-sm font-bold">
          {loading || loadingHierarchy ? '...' : `${filteredShops.length} 件`}
        </span>
      </div>

      {/* Loading */}
      {(loading || loadingHierarchy) && (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line text-2xl text-neon-pink animate-spin" />
        </div>
      )}

      {!loading && !loadingHierarchy && (
        <>
          {/* Category Buttons */}
          <div className="relative mb-5">
            <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
            <div
              className="flex gap-2 overflow-x-auto pb-2 px-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {categories.map((cat) => {
                const isActive = activeCategory === cat;
                const color = getCategoryColor(cat);
                const icon = getCategoryIcon(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => handleCategoryClick(cat)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full border whitespace-nowrap text-xs font-bold tracking-wider transition-all duration-300 cursor-pointer flex-shrink-0 ${
                      isActive
                        ? 'bg-white/10 text-white'
                        : 'bg-transparent text-gray-500 border-white/10 hover:bg-white/5 hover:text-gray-300'
                    }`}
                    style={{
                      borderColor: isActive ? `${color}60` : undefined,
                      boxShadow: isActive ? `0 0 16px ${color}20` : undefined,
                    }}
                  >
                    <i
                      className={`${icon} text-sm w-4 h-4 flex items-center justify-center`}
                      style={{ color: isActive ? color : undefined }}
                    />
                    <span>{cat === 'ALL' ? 'すべて' : cat}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Shop Cards */}
          <div className="relative">
            <div className="hidden sm:block absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
            <div className="hidden sm:block absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />

            {/* Mobile: 1列縦並び / PC: 横スクロール */}
            <div className="sm:hidden flex flex-col gap-4">
              {filteredShops.map((shop) => {
                const isSelected = selectedShopId === shop.id;
                const icon = shop.attributeName
                  ? (attributeIconMap[shop.attributeName] || 'ri-store-2-line')
                  : (categoryIconMap[shop.category] || 'ri-store-2-line');
                const displayColor = shop.attributeColor || shop.color;

                return (
                  <button
                    key={shop.id}
                    onClick={() => handleShopClick(shop)}
                    className={`w-full text-left rounded-xl border transition-all duration-300 cursor-pointer group overflow-hidden ${
                      isSelected
                        ? 'bg-white/[0.06]'
                        : 'bg-black/40 border-white/5 hover:bg-white/[0.04] hover:border-white/15'
                    }`}
                    style={{
                      borderColor: isSelected ? `${displayColor}60` : undefined,
                      boxShadow: isSelected ? `0 0 24px ${displayColor}15` : undefined,
                    }}
                  >
                    <div className="relative w-full h-[160px] overflow-hidden">
                      {shop.thumbnail ? (
                        <img src={shop.thumbnail} alt={shop.name} className="w-full h-full object-cover object-top" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${displayColor}20, ${displayColor}05)` }}>
                          <i className={`${icon} text-4xl`} style={{ color: `${displayColor}60` }} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-neon-green/20 border border-neon-green/40">
                        <span className="text-neon-green text-[8px] font-bold tracking-wider">REGISTERED</span>
                      </div>
                      <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-bold tracking-wider backdrop-blur-sm flex flex-col items-end gap-0.5" style={{ background: `${displayColor}30`, border: `1px solid ${displayColor}40` }}>
                        {shop.attributeName && <span style={{ color: displayColor }}>{shop.attributeName}</span>}
                        {!shop.attributeName && <span style={{ color: displayColor }}>{shop.category}</span>}
                      </div>
                      {/* お気に入りボタン：右下・大きめサイズ */}
                      <div
                        className="absolute bottom-3 right-3 z-20"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FavoriteButton artistId={shop.userId} artistType="shop" size="md" />
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="text-white text-sm font-bold tracking-wide mb-2 truncate">{shop.name}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2">{shop.description || '詳細情報は店舗ページをご確認ください'}</p>
                      <div className="flex items-center gap-1.5 pt-3 border-t border-white/5">
                        <i className="ri-map-pin-line text-gray-600 text-xs flex-shrink-0" />
                        <span className="text-gray-600 text-[11px] truncate">{shop.address}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
              {filteredShops.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <i className="ri-store-2-line text-3xl text-gray-700 mb-3" />
                  <p className="text-gray-600 text-sm">このカテゴリの店舗はまだありません</p>
                </div>
              )}
            </div>

            {/* PC: 横スクロール */}
            <div
              ref={scrollRef}
              className="hidden sm:flex gap-4 overflow-x-auto pb-4 px-1 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {filteredShops.map((shop) => {
                const isSelected = selectedShopId === shop.id;
                const icon = shop.attributeName 
                  ? (attributeIconMap[shop.attributeName] || 'ri-store-2-line')
                  : (categoryIconMap[shop.category] || 'ri-store-2-line');
                const displayColor = shop.attributeColor || shop.color;

                return (
                  <button
                    key={shop.id}
                    onClick={() => handleShopClick(shop)}
                    className={`flex-shrink-0 w-[260px] sm:w-[280px] text-left rounded-xl border transition-all duration-300 cursor-pointer group snap-start overflow-hidden ${
                      isSelected
                        ? 'bg-white/[0.06]'
                        : 'bg-black/40 border-white/5 hover:bg-white/[0.04] hover:border-white/15'
                    }`}
                    style={{
                      borderColor: isSelected ? `${displayColor}60` : undefined,
                      boxShadow: isSelected ? `0 0 24px ${displayColor}15` : undefined,
                    }}
                  >
                    {/* Thumbnail */}
                    <div className="relative w-full h-[140px] overflow-hidden">
                      {shop.thumbnail ? (
                        <img
                          src={shop.thumbnail}
                          alt={shop.name}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${displayColor}20, ${displayColor}05)` }}
                        >
                          <i className={`${icon} text-4xl`} style={{ color: `${displayColor}60` }} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-neon-green/20 border border-neon-green/40">
                        <span className="text-neon-green text-[8px] font-bold tracking-wider">REGISTERED</span>
                      </div>
                      <div
                        className="absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-bold tracking-wider backdrop-blur-sm flex flex-col items-end gap-0.5"
                        style={{
                          background: `${displayColor}30`,
                          border: `1px solid ${displayColor}40`,
                        }}
                      >
                        {shop.attributeName && (
                          <span style={{ color: displayColor }}>{shop.attributeName}</span>
                        )}
                        {shop.mainGenreName && (
                          <span className="text-[8px] opacity-80" style={{ color: displayColor }}>
                            → {shop.mainGenreName}
                          </span>
                        )}
                        {shop.mainPartName && (
                          <span className="text-[8px] opacity-80" style={{ color: displayColor }}>
                            → {shop.mainPartName}
                          </span>
                        )}
                        {!shop.attributeName && (
                          <span style={{ color: displayColor }}>{shop.category}</span>
                        )}
                      </div>
                      <div className="absolute bottom-3 left-3 z-20">
                        <FavoriteButton artistId={shop.userId} artistType="shop" size="sm" />
                      </div>
                      <div
                        className="absolute bottom-3 right-3 w-9 h-9 rounded-lg flex items-center justify-center backdrop-blur-sm"
                        style={{ background: `${displayColor}20`, border: `1px solid ${displayColor}40` }}
                      >
                        <i className={`${icon} text-base`} style={{ color: displayColor }} />
                      </div>
                    </div>

                    <div className="p-4 sm:p-5">
                      <h4 className="text-white text-sm font-bold tracking-wide mb-2 truncate">{shop.name}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2 min-h-[2.5rem]">
                        {shop.description || '詳細情報は店舗ページをご確認ください'}
                      </p>
                      <div className="flex items-center gap-1.5 pt-3 border-t border-white/5">
                        <i className="ri-map-pin-line text-gray-600 text-xs flex-shrink-0" />
                        <span className="text-gray-600 text-[11px] truncate">{shop.address}</span>
                      </div>
                    </div>
                  </button>
                );
              })}

              {filteredShops.length === 0 && (
                <div className="flex-shrink-0 w-full flex flex-col items-center justify-center py-12 text-center">
                  <i className="ri-store-2-line text-3xl text-gray-700 mb-3" />
                  <p className="text-gray-600 text-sm">このカテゴリの店舗はまだありません</p>
                </div>
              )}
            </div>
          </div>

          {filteredShops.length > 1 && (
            <div className="hidden sm:flex items-center justify-center gap-2 mt-2">
              <i className="ri-arrow-left-right-line text-gray-700 text-xs" />
              <span className="text-gray-700 text-[10px] tracking-wider">SWIPE</span>
            </div>
          )}
        </>
      )}

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
